import { useContext, useState } from 'react'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease, LeaseApp } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CopyButton } from '@/components/CopyButton'
import { Err } from '@/components/Err'
import { mayChange } from '@/domain/lease'
import { SessionCtx } from '@/hooks/useSession'

// 기계가 공개하는 앱(D-5): HTTP 포트 하나. 한 줄이다 — 밖에서 열리는 주소(링크와 복사), 포트 입력, 저장.
// 경로를 나누지 않는다 — `/api` 를 포함한 모든 요청이 그 포트로 간다.
// 임대가 active 가 아니면 바꿀 수 없다(백엔드가 409 로 거절하므로 입력을 잠근다).
// 그 VM 을 바꿀 수 없는 사람(`mayChange` — 관리자가 보는 자기 팀 사람의 VM)에게는 포트가 잠긴 채 보이고 `저장` 이 없다.

export function AppPanel({ lease, app, onSaved }: { lease: Lease; app: LeaseApp; onSaved: () => void }) {
  const viewer = useContext(SessionCtx)
  const saves = mayChange(lease, viewer)
  const locked = lease.state !== 'active' || !saves
  const saved = app.port
  const [text, setText] = useState(portText(saved))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const dirty = text !== portText(saved)
  const url = app.url && saved ? app.url : null

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await leaseApi.setApp(lease.id, { port: portValue(text) })
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} className="grid gap-1.5 rounded-lg border bg-card px-4 py-3">
      <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">웹</span>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 basis-64 items-center gap-1 rounded-md bg-surface-2 py-1 pr-1 pl-3">
          {url ? (
            <a href={url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate py-1 font-mono text-[12px] hover:underline">
              {url}
            </a>
          ) : (
            <span className="flex-1 py-1 text-[12px] text-muted-foreground">
              {saved ? '주소 없음' : '공개하지 않음'}
            </span>
          )}
          {url ? <CopyButton text={url} label="앱 주소 복사" icon /> : null}
        </div>
        <Label htmlFor="app-port" className="text-[11px] text-muted-foreground">
          포트
        </Label>
        <Input
          id="app-port"
          aria-label="앱 포트"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="w-24"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={locked || busy}
        />
        {saves ? (
          <Button type="submit" size="sm" variant="outline" aria-label="앱 포트 저장" disabled={locked || busy || !dirty}>
            저장
          </Button>
        ) : null}
      </div>
      <Err error={error} />
    </form>
  )
}

function portText(p: number | null | undefined): string {
  return p ? String(p) : ''
}

function portValue(text: string): number | null {
  const n = Number(text.trim())
  return text.trim() && Number.isInteger(n) ? n : null
}
